/*eslint-disable @typescript-eslint/explicit-module-boundary-types*/

import {
    BusinessType,
    MerchantOnboardingControlPersonRequest,
    MerchantOnboardingUpdateBeneficiaryRequest,
    MerchantRequestDto,
    MerchantResponseDto,
} from "@/api/paymentApi";
import {AppDefaults, Country} from "@/app/utils/common/constants";
import {
    formatPhoneNumber,
    formatZip,
    msToyyyyMMdd,
    unFormatPhoneNumber,
    unHyphenify,
} from "@/app/utils/common/functions/functions";
import {validateField} from "@/app/utils/common/functions/validation";
import validations from "@/app/validators/api/openapi";
import {beneficiaryTaxId, stateCode} from "@/app/validators/countrySpecificValidationSpec";
import i18n from '@/i18n';
import {assertShouldShowTaxIdfield} from "@/store/modules/onboarding/conditionalApiFields/conditionHelpers";
import {
    ApiData,
    ApiField,
    CountryBasedApiField,
    OnboardingSectionWithArrayData,
} from "@/store/modules/onboarding/onboardingTypes";
import {copyFromApiResponse} from "@/store/storeMappers";
import {validateAndGenerateErrorForStakeHolder} from "@/store/validationFromStore.js";
import FlattenUtil from 'flat';
import cloneDeep from 'lodash.clonedeep';
import Vue from "vue";

const Keys = {
    ownership: 'ownership',
}

const invalidOwnershipError = {
    key: Keys.ownership,
    errors: i18n.t('errors.validation_error.controlPersonExpected'),
};

const invalidOwnerTypeError = {key: 'ownerType', errors: i18n.t('errors.validation_error.presence')}
const invalidBusinessName = {key: 'businessName', errors: i18n.t('errors.validation_error.presence')}

const ownershipPercentageError = (maxAllowedNumber: number) => ({
    key: 'ownershipPercentage',
    errors: i18n.t('errors.validation_error.sumOfPercentagesExceedsHundred', {num: maxAllowedNumber}),
});

const createLabelAndInstruction = () => {
    const result = {
        value: null,
        valid: false,
        label: {},
        instruction: {},
        validationSpec: beneficiaryTaxId,
        shouldShowInView: (country) => assertShouldShowTaxIdfield(country),
    };

    Object.keys(Country).forEach((country) => {
        const countryLowerCase = country.toLowerCase();
        result.label[country] = `addBeneficiary.taxId.${countryLowerCase}.label`;
        result.instruction[country] = `addBeneficiary.taxId.${countryLowerCase}.instruction`;
    });
    result.label["default"] = "addBeneficiary.taxId.label";
    result.instruction["default"] = "addBeneficiary.taxId.instruction";

    return result;
};

const controlPersonCountrySpecificFields = {
    taxId: {
        apiPath: "tax_info.tax_id",
        ...createLabelAndInstruction()
    },
    addressState: {
        apiPath: "personal_address.state_code",
        value: null,
        valid: false,
        validationSpec: stateCode
    },
}

export enum StakeholderField {
    OWNER_TYPE = "ownerType",
    BUSINESS_NAME = "businessName",
    ID = "id",
    DRAFT_ID = "draftId",
    ID_AT_PAYFAC = "idAtPayfac",
    FIRST_NAME = "firstName",
    LAST_NAME = "lastName",
    JOB_TITLE = "jobTitle",
    DATE_OF_BIRTH = "dateOfBirth",
    EMAIL_ADDRESS = "emailAddress",
    PHONE_NUMBER = "phoneNumber",
    ADDRESS_LINE1 = "addressLine1",
    ADDRESS_LINE2 = "addressLine2",
    ADDRESS_CITY = "addressCity",
    ADDRESS_STATE = "addressState",
    ADDRESS_ZIP = "addressZip",
    ADDRESS_COUNTRY = "addressCountry",
    OWNERSHIP_PERCENTAGE = "ownershipPercentage",
    REGISTRATION_NUMBER = "registrationNumber",
    VAT_NUMBER = "vatNumber",
    TAX_ID = "taxId",
}

export const defaultControlPerson = {
    [StakeholderField.FIRST_NAME]: {apiPath: "first_name", value: null, valid: false},
    [StakeholderField.LAST_NAME]: {apiPath: "last_name", value: null, valid: false},
    [StakeholderField.JOB_TITLE]: {apiPath: "title", value: null, valid: false},
    [StakeholderField.DATE_OF_BIRTH]: { apiPath: "dob", value: null, valid: false, },
    [StakeholderField.EMAIL_ADDRESS]: {apiPath: "email", value: null, valid: false},
    [StakeholderField.PHONE_NUMBER]: { apiPath: "phone", value: null, formattedValue: undefined, valid: false, },
    [StakeholderField.ADDRESS_LINE1]: {apiPath: "personal_address.line1", value: null, valid: false},
    [StakeholderField.ADDRESS_LINE2]: {apiPath: "personal_address.line2", value: null, valid: true},
    [StakeholderField.ADDRESS_CITY]: {apiPath: "personal_address.city", value: null, valid: false},
    [StakeholderField.ADDRESS_ZIP]: {apiPath: "personal_address.postal_code", value: null, valid: false},
    [StakeholderField.ADDRESS_COUNTRY]: {apiPath: "personal_address.country", value: AppDefaults.country, valid: false},
    [StakeholderField.OWNERSHIP_PERCENTAGE]: {apiPath: "principal_percentage_ownership", value: null, valid: false},
    [StakeholderField.REGISTRATION_NUMBER]: {apiPath: "tax_info.registration_number", value: null, valid: true},
    [StakeholderField.VAT_NUMBER]: {apiPath: "tax_info.vat_number", value: null, valid: true},
    ...controlPersonCountrySpecificFields
}

const additionalBeneficiaryCountrySpecificFields = {
    [StakeholderField.TAX_ID]: {
        apiPath: "additional_beneficiaries.*.tax_info.tax_id",
        ...createLabelAndInstruction()
    },
    [StakeholderField.ADDRESS_STATE]: {
        apiPath: "additional_beneficiaries.*.personal_address.state_code",
        value: null,
        valid: false,
        validationSpec: stateCode
    },
}

export const defaultBeneficiaryDetails = (): ApiData => ({
    [StakeholderField.OWNER_TYPE]: { apiPath: "additional_beneficiaries.*.business_owner_type", value: null, valid: false},
    [StakeholderField.BUSINESS_NAME]: { apiPath: "additional_beneficiaries.*.business_name", value: null, valid: false},
    [StakeholderField.ID]: {apiPath: "additional_beneficiaries.*.id", value: null, valid: true},
    [StakeholderField.DRAFT_ID]: {apiPath: "additional_beneficiaries.*.draft_id", value: null, valid: true},
    [StakeholderField.ID_AT_PAYFAC]: {apiPath: "additional_beneficiaries.*.id_at_payfac", value: null, valid: true},
    [StakeholderField.FIRST_NAME]: {apiPath: "additional_beneficiaries.*.first_name", value: null, valid: false},
    [StakeholderField.LAST_NAME]: {apiPath: "additional_beneficiaries.*.last_name", value: null, valid: false},
    [StakeholderField.JOB_TITLE]: {apiPath: "additional_beneficiaries.*.title", value: null, valid: false},
    [StakeholderField.DATE_OF_BIRTH]: { apiPath: "additional_beneficiaries.*.dob", value: null, valid: false, },
    [StakeholderField.EMAIL_ADDRESS]: {apiPath: "additional_beneficiaries.*.email", value: null, valid: false},
    [StakeholderField.PHONE_NUMBER]: { apiPath: "additional_beneficiaries.*.phone", value: null, formattedValue: null, valid: false },
    [StakeholderField.ADDRESS_LINE1]: {apiPath: "additional_beneficiaries.*.personal_address.line1", value: null, valid: false},
    [StakeholderField.ADDRESS_LINE2]: {apiPath: "additional_beneficiaries.*.personal_address.line2", value: null, valid: true},
    [StakeholderField.ADDRESS_CITY]: {apiPath: "additional_beneficiaries.*.personal_address.city", value: null, valid: false},
    [StakeholderField.ADDRESS_ZIP]: {apiPath: "additional_beneficiaries.*.personal_address.postal_code", value: null, formattedValue: null, valid: false},
    [StakeholderField.ADDRESS_COUNTRY]: {apiPath: "additional_beneficiaries.*.personal_address.country", value: AppDefaults.country, valid: false},
    [StakeholderField.OWNERSHIP_PERCENTAGE]: {apiPath: "additional_beneficiaries.*.principal_percentage_ownership", value: null, valid: false},
    [StakeholderField.REGISTRATION_NUMBER]: {apiPath: "additional_beneficiaries.*.tax_info.registration_number", value: null, valid: true},
    [StakeholderField.VAT_NUMBER]: {apiPath: "additional_beneficiaries.*.tax_info.vat_number", value: null, valid: true},
    ...cloneDeep(additionalBeneficiaryCountrySpecificFields)
});

export const stakeholderDetailsDefaultState: OnboardingSectionWithArrayData = {
    api: "createMerchant",
    errors: {},
    data: [cloneDeep(defaultControlPerson)],
    draft: [cloneDeep(defaultControlPerson)],
};

const businessProfileGetters = {
    getControlPerson: s => s.data[0],
    isControlPersonValid: s => Object.keys(s.data[0]).every(key => s.data[0][key].valid),
    getControlPersonName: s => `${s.data[0].firstName.value} ${s.data[0].lastName.value}`,
    getControlPhoneNumber: s => `${s.data[0].phoneNumber.value}`,
    getBeneficiaries: s => s.data,
    getAdditionalBeneficiaries: s => s.data.slice(1),
    getApi: s => s.api,
    getErrors: s => s.errors,
    getErrorByField: s => (i, field) => {
        const apiPath = s.data[i][field].apiPath;
        return s.errors[field] ?? s.errors[apiPath.replace("additional_beneficiaries.*.", "")];
    },
    totalInvalidFields: s => {
        return s.data.reduce((agg, data) => {
            const invalidFields = Object.keys(data).filter(key => data[key].valid === false);
            return agg + invalidFields.length;
        }, 0);
    },
    totalValidFields: s => {
        return s.data.reduce((agg, data) => {
            const validFields = Object.keys(data).filter(key => data[key].valid === true);
            return agg + validFields.length;
        }, 0);
    },
    getOwnershipErrors: s => s.errors.ownership,
    getBeneficialOwnerType: s => (i) => s.data[i]?.ownerType,
    getBusinessName: s => (i) => s.data[i]?.businessName,
    getId: s => (i) => s.data[i]?.id,
    getFirstName: s => (i) => s.data[i]?.firstName,
    getLastName: s => (i) => s.data[i]?.lastName,
    getJobTitle: s => (i) => s.data[i]?.jobTitle,
    getDateOfBirth: s => (i) => s.data[i]?.dateOfBirth,
    getEmailAddress: s => (i) => s.data[i]?.emailAddress,
    getPhoneNumber: s => (i) => s.data[i]?.phoneNumber,
    getAddressLine1: s => (i) => s.data[i]?.addressLine1,
    getAddressLine2: s => (i) => s.data[i]?.addressLine2,
    getAddressCity: s => (i) => s.data[i]?.addressCity,
    getAddressState: s => (i) => s.data[i]?.addressState,
    getAddressZip: s => (i) => s.data[i]?.addressZip,
    getAddressCountry: s => (i) => s.data[i]?.addressCountry,
    getOwnershipPercentage: s => (i) => s.data[i]?.ownershipPercentage,
    getTaxId: s => (i) => s.data[i]?.taxId,
    shouldShowApiField: s => (apiFieldName, i) => (s.data[i][apiFieldName] as CountryBasedApiField)
      .shouldShowInView(s.data[i].addressCountry.value),
    isBeneficiaryValid: s => (i) => {
        return Object.keys(s.data[i])
            .reduce((agg, key) => agg && s.data[i][key].valid, true);
    },
    getCurrentSumOfOwnershipPercentages: (state) => {
        return state.data.reduce((sum, a) => {
            sum += parseInt(a.ownershipPercentage.value) || 0;
            return sum;
        }, 0)
    },
    canAddAdditionalBeneficiary: (state, getters, rootState, rootGetters) => {
        return rootGetters['essentials/getTypeOfBusiness'] !== BusinessType.INDIVIDUALSOLEPROPRIETOR 
    },
    canAddMoreBeneficiaries: (state) => {
      return state.data.length < AppDefaults.maxNumberOfBeneficiaries;
    },

    getNumberOfValidStackholders: (state, getters) =>
        state.data.filter((_x, i) => getters.isBeneficiaryValid(i)).length,

    isValid: (_state, getters) => {
        const atleastOneBeneficiaryPresent = getters.getBeneficiaries.length > 0;
        const allBeneficiariesValid = getters.getBeneficiaries.every((_x, i) => getters.isBeneficiaryValid(i));
        return atleastOneBeneficiaryPresent && allBeneficiariesValid;
    },
    getIncrementalOnboardignControlPerson: (s): MerchantOnboardingControlPersonRequest => {
        return {
            first_name: s.data[0].firstName.value,
            last_name: s.data[0].lastName.value,
            title: s.data[0].jobTitle.value,
            dob: s.data[0].dateOfBirth.value,
            email: s.data[0].emailAddress.value,
            phone: s.data[0].phoneNumber.value,
            tax_info: {
                tax_id: s.data[0].taxId.value,
                registration_number: s.data[0].registrationNumber.value,
                vat_number: s.data[0].vatNumber.value
            },
            personal_address: {
                line1: s.data[0].addressLine1.value,
                line2: s.data[0].addressLine2.value,
                city: s.data[0].addressCity.value,
                state_code: s.data[0].addressState.value,
                postal_code: s.data[0].addressZip.value,
                country: s.data[0].addressCountry.value
            },
        };
    },
    getIncrementalOnboardingAdditionalBeneficiary: (s) => {
        return (i): MerchantOnboardingUpdateBeneficiaryRequest => {
            const data = s.data[i];
            return {
                draft_id: data.draftId.value,
                business_owner_type: data.ownerType.value,
                business_name: data.businessName.value,
                first_name: data.firstName.value,
                last_name: data.lastName.value,
                title: data.jobTitle.value,
                dob: data.dateOfBirth.value,
                email: data.emailAddress.value,
                phone: data.phoneNumber.value,
                tax_info: {
                    tax_id: data.taxId.value,
                    registration_number: data.registrationNumber.value,
                    vat_number: data.vatNumber.value
                },
                personal_address: {
                    line1: data.addressLine1.value,
                    line2: data.addressLine2.value,
                    city: data.addressCity.value,
                    state_code: data.addressState.value,
                    postal_code: data.addressZip.value,
                    country: data.addressCountry.value
                },
            };
        }
    }
}

const validateApiField = (commit, api, data, index, key) => {
    const countrySpecificValidationSpec = data[index][key].validationSpec;
    const validationSpec = countrySpecificValidationSpec ?
      data[index][key].validationSpec[data[index]["addressCountry"].value] : validations[api][data[index][key].apiPath];

    const errors = validateField(data[index][key].value, validationSpec);
    const action = errors.length ? 'setInvalid' : 'setValid';
    commit(action, {index, key})
}

export const controlPersonPercentageOwnership = {
    min: 0,
    max: 100,
}

export const additionalOwnerPercentageOwnership = {
    min: 25,
    max: 100,
}

const validateBusinessOwnerType = (data: any,isControlPerson) => {
    return isControlPerson || data.ownerType.value !== null
}

const validateBusinessName = (data: any,isControlPerson) => {
    return isControlPerson || data.ownerType.value !== 'ENTITY' || data.businessName.value !== null
}

function isIndividualControlPersonCountryValid(rootGetters) {
    if (rootGetters["essentials/getTypeOfBusiness"] === BusinessType.INDIVIDUALSOLEPROPRIETOR) {
        return rootGetters["essentials/getCountry"] === rootGetters["stakeholdersDetails/getControlPerson"]?.addressCountry.value 
    }
    return true;
}

const actions = {
    validateControlPersonPresent({commit, getters}) {
        const controlPersonPresent = getters.isControlPersonValid;
        if (!controlPersonPresent) {
            commit('setErrors', invalidOwnershipError)
        }
        return controlPersonPresent
    },
    validateControlPersonCountry({dispatch, rootGetters, commit}) {
        dispatch('validateField', {index: 0, key: StakeholderField.ADDRESS_COUNTRY});
        if (!isIndividualControlPersonCountryValid(rootGetters)) {
            commit('setErrors', {
                key: StakeholderField.ADDRESS_COUNTRY, 
                errors: i18n.t('errors.validation_error.businessCountryMismatch', {country: rootGetters["essentials/getCountry"]})})
        }
    },
    validate({commit, state, getters, rootGetters}, i) {
        commit('resetErrors')
        const data = state.data[i];
        const isControlPerson = i === 0
        let validationResult = validateAndGenerateErrorForStakeHolder(commit, stakeholderDetailsDefaultState.api, getters.getAddressCountry(i).value, data);
        const ownerTypeValidation = validateBusinessOwnerType(data, isControlPerson);
        const businessNameValidation = validateBusinessName(data, isControlPerson);
        
        if (isControlPerson && !isIndividualControlPersonCountryValid(rootGetters)) {
            commit('setErrors', {
                key: StakeholderField.ADDRESS_COUNTRY, 
                errors: i18n.t('errors.validation_error.businessCountryMismatch', {country: rootGetters["essentials/getCountry"]})})
            validationResult = false;
        }
        

        if (!ownerTypeValidation) {
            commit('setErrors', invalidOwnerTypeError)
            validationResult = false;
        }

        if (!businessNameValidation) {
            commit('setErrors', invalidBusinessName)
            validationResult = false;
        }

        return validationResult;
    },
    validateField({commit, state, rootGetters}, {index, key}) {
        const data = state.data[index];
        const isControlPerson = index === 0;
        switch (key) {
            case StakeholderField.ADDRESS_COUNTRY: {
                if (!isIndividualControlPersonCountryValid(rootGetters) && isControlPerson) {
                    commit('setInvalid', {index, key})
                    return;
                }
                break;
            }
            case "ownerType": {
                if (!validateBusinessOwnerType(data, isControlPerson)) {
                    commit('setInvalid', {index, key})
                    return;
                }
                break;
            }
            case "businessName": {
                if (!validateBusinessName(data, isControlPerson)) {
                    commit('setInvalid', {index, key})
                    return;
                }
            }
        }
        validateApiField(commit, stakeholderDetailsDefaultState.api, state.data, index, key)
    },
    validateAllFields({state, dispatch}) {
        state.data.forEach((data, index) => {
            Object.keys(data).forEach(key => dispatch('validateField', {index, key}))
        })
    },
    setErrors({commit}, {key, errors}) {
        commit('setErrors', {key, errors})
    },
    copyDraftToData({commit}) {
        commit('copyDraftToData')
    },
    copyDataToDraft({commit}) {
        commit('copyDataToDraft')
    },
    createStakeholder({commit, dispatch, state}) {
        commit('createStakeholder')
        const beneficiaryIndex = state.data.length - 1;
        commit('setAddressCountry', {i: beneficiaryIndex, value: state.data[0].addressCountry.value})
        commit('setAddressState', {i: beneficiaryIndex, value: null})
        commit('setTaxId', {i: beneficiaryIndex, value: null})
        dispatch('validateAllFields')
        commit('copyDataToDraft')
        return beneficiaryIndex;
    },
    clearData({commit}) {
        commit('clearData')
    },
    clearErrors({commit}) {
        commit('clearErrors')
    },
    deleteStakeholder({commit}, i) {
        commit('deleteStakeholder', i)
    },
    resetControlPerson({commit}) {
        commit('resetControlPerson')
    },
    updateStakeholder({commit}, {i, beneficiary}) {
        commit('updateStakeholder', {i, beneficiary})
    },
    setDraftId({commit}, {i, value}) {
        commit('setDraftId', {i, value})
    },
    setOwnerType({commit, dispatch}, {i, value}) {
        commit('setOwnerType', {i, value})
        dispatch('validateField', {index: i, key: 'ownerType'})
    },
    setBusinessName({commit, dispatch}, {i, event}) {
        commit('setBusinessName', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'businessName'})
    },
    setFirstName({commit, dispatch}, {i, event}) {
        commit('setFirstName', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'firstName'})
    },
    setLastName({commit, dispatch}, {i, event}) {
        commit('setLastName', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'lastName'})
    },
    setJobTitle({commit, dispatch}, {i, event}) {
        commit('setJobTitle', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'jobTitle'})
    },
    setDateOfBirth({commit, dispatch}, {i, value}) {
        commit('setDateOfBirth', {i, value})
        dispatch('validateField', {index: i, key: 'dateOfBirth'})
    },
    setEmailAddress({commit, dispatch}, {i, event}) {
        commit('setEmailAddress', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'emailAddress'})
    },
    setPhoneNumber({commit, dispatch}, {i, value, countryCode}) {
        commit('setPhoneNumber', {i, value, countryCode})
        dispatch('validateField', {index: i, key: 'phoneNumber'})
    },
    setAddressLine1({commit, dispatch}, {i, event}) {
        commit('setAddressLine1', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'addressLine1'})
    },
    setAddressLine2({commit}, {i, event}) {
        commit('setAddressLine2', {i, value: event.target.value})
    },
    setAddressCity({commit, dispatch}, {i, event}) {
        commit('setAddressCity', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'addressCity'})
    },
    setAddressState({commit, dispatch}, {i, value}) {
        commit('setAddressState', {i, value})
        dispatch('validateField', {index: i, key: 'addressState'})
    },
    setAddressZip({commit, dispatch}, {i, event}) {
        commit('setAddressZip', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'addressZip'})
    },
    setAddressCountry({commit, dispatch}, {i, value}) {
        commit('setAddressCountry', {i, value})
        dispatch('validateField', {index: i, key: 'addressCountry'})
    },
    setOwnershipPercentage({commit, state, dispatch}, {i, event}) {
        const value = event.target.value;
        commit('setOwnershipPercentage', {i, value: value})
        let sumOfPercentagesOfOtherStakeholders = 0;
        for (let j = 0; j < state.data.length; j++) {
            if (j === i) continue;
            sumOfPercentagesOfOtherStakeholders += parseInt(state.data[j].ownershipPercentage.value) || 0;
        }
        const maxAllowedNumber = 100 - sumOfPercentagesOfOtherStakeholders;
        if (parseInt(value) > maxAllowedNumber) {
            commit('setErrors', ownershipPercentageError(maxAllowedNumber))
            commit('setInvalid', {index: i, key: 'ownershipPercentage'})
        } else {
            commit('resetErrorByKey', 'ownershipPercentage')
            dispatch('validateField', {index: i, key: 'ownershipPercentage'})
        }
    },
    setTaxId({commit, dispatch}, {i, event}) {
        commit('setTaxId', {i, value: event?.target?.value})
        dispatch('validateField', {index: i, key: 'taxId'})
    },
    setRegistrationNumber({commit, dispatch}, {i, event}) {
        commit('setRegistrationNumber', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'taxId'})
    },
    setVatNumber({commit, dispatch}, {i, event}) {
        commit('setVatNumber', {i, value: event.target.value})
        dispatch('validateField', {index: i, key: 'taxId'})
    },
    copyFromMerchantApiResponse({commit}, merchantApiResponse) {
        commit('clearData')
        commit('copyControlOwnerFromMerchantApiResponse', merchantApiResponse)
        commit('copyAdditionalBeneficiariesFromMerchantApiResponse', merchantApiResponse)
    },
    copyAdditionalBeneficiariesFromMerchantApiResponse({commit, state, dispatch}, merchantApiResponse) {
        commit('copyAdditionalBeneficiariesFromMerchantApiResponse', merchantApiResponse)
        state.data.forEach((data, index) => {
            Object.keys(data).forEach(key => dispatch('validateField', {index, key}))
        })
    }
};

const mutations = {
    resetErrors(state): void {
        state.errors = {}
    },
    resetErrorByKey(state, key): void {
        delete state.errors[key];
    },
    setInvalid(state, {index, key}) {
        state.data[index][key].valid = false;
    },
    setValid(state, {index, key}) {
        state.data[index][key].valid = true;
    },
    setErrors(state, {key, errors}): void {
        Vue.set(state.errors, key, errors);
    },
    copyDraftToData(state): void {
        Vue.set(state, 'data', state.draft)
    },
    copyDataToDraft(state): void {
        Vue.set(state, 'draft', state.data)
    },
    createStakeholder(state): void {
        state.data.push(cloneDeep(defaultBeneficiaryDetails()))
    },
    clearData(state): void {
        Vue.set(state, 'data', [])
        Vue.set(state.data, 0, cloneDeep(defaultControlPerson))
    },
    clearErrors(state): void {
        state.errors = {}
    },
    updateStakeholder(state, {i, beneficiary}) {
        Object.keys(state.data[i]).forEach(key => {
            Vue.set(state.data[i][key], 'value', beneficiary[key]?.value);
        });
    },
    deleteStakeholder(state, i): void {
        state.data.splice(i, 1);
    },
    resetControlPerson(state) : void {
        Vue.set(state.data, 0, cloneDeep(defaultControlPerson))
    },
    setOwnerType(state, {i, value}): void {
        state.data[i].ownerType.value = value;
        delete state.errors.ownerType;
    },
    setDraftId(state, {i, value}): void {
        state.data[i].draftId.value = value;
        delete state.errors.draftId;
    },
    setBusinessName(state, {i, value}): void {
        state.data[i].businessName.value = value;
        delete state.errors.businessName;
    },
    setFirstName(state, {i, value}): void {
        state.data[i].firstName.value = value;
        delete state.errors.firstName;
    },
    setLastName(state, {i, value}): void {/**/
        state.data[i].lastName.value = value;
        delete state.errors.lastName;
    },
    setJobTitle(state, {i, value}): void {
        state.data[i].jobTitle.value = value;
        delete state.errors.jobTitle;
    },
    setDateOfBirth(state, {i, value}): void {
        state.data[i].dateOfBirth.value = msToyyyyMMdd(value);
        delete state.errors.dateOfBirth;
    },
    setEmailAddress(state, {i, value}): void {
        state.data[i].emailAddress.value = value.trim();
        delete state.errors.emailAddress;
    },
    setPhoneNumber(state, {i, value, countryCode}): void {
        state.data[i].phoneNumber.value = `${countryCode} ${unFormatPhoneNumber(value)}`;
        state.data[i].phoneNumber.formattedValue = value;
        delete state.errors.phoneNumber;
    },
    setAddressLine1(state, {i, value}): void {
        state.data[i].addressLine1.value = value;
        delete state.errors.addressLine1;
    },
    setAddressLine2(state, {i, value}): void {
        state.data[i].addressLine2.value = value;
        delete state.errors.addressLine2;
    },
    setAddressCity(state, {i, value}): void {
        state.data[i].addressCity.value = value;
        delete state.errors.addressCity;
    },
    setAddressState(state, {i, value}): void {
        state.data[i].addressState.value = value;
        delete state.errors.addressState;
    },
    setAddressZip(state, {i, value}): void {
        state.data[i].addressZip.value = unHyphenify(value);
        delete state.errors.addressZip;
    },
    setAddressCountry(state, {i, value}): void {
        state.data[i].addressCountry.value = value;
        delete state.errors.addressCountry;
    },
    setOwnershipPercentage(state, {i, value}): void {
        state.data[i].ownershipPercentage.value = value;
        delete state.errors.ownershipPercentage;
    },
    setTaxId(state, {i, value}): void {
        state.data[i].taxId.value = value;
        delete state.errors.taxId;
    },
    setRegistrationNumber(state, {i, value}): void {
        state.data[i].registrationNumber.value = unHyphenify(value);
        delete state.errors.taxId;
    },
    setVatNumber(state, {i, value}): void {
        state.data[i].vatNumber.value = unHyphenify(value);
        delete state.errors.taxId;
    },
    copyControlOwnerFromMerchantApiResponse(state, merchantApiResponse: MerchantResponseDto): void {
        copyControlPersonFromMerchantApiResponse(merchantApiResponse, state.data[0]);
    },
    copyAdditionalBeneficiariesFromMerchantApiResponse(state, merchantApiResponse: MerchantResponseDto): void {
        Vue.set(state, 'data', [state.data[0]])
        merchantApiResponse.additional_beneficiaries?.forEach((_, index) => {
            const newOwner = copyAdditionalBeneficiaryFromMerchantApiResponse(merchantApiResponse, index);
            state.data.push(newOwner);
        })
    },
};

function copyControlPersonFromMerchantApiResponse(apiResponse: MerchantRequestDto, controlPersonData: any) {
    const newOwner = copyFromApiResponse(apiResponse, controlPersonData);
    const {formattedValue} = parsePhoneNumber(newOwner.phoneNumber);
    newOwner.phoneNumber = {...newOwner.phoneNumber, formattedValue};
    (newOwner.addressZip as ApiField).formattedValue = formatZip(newOwner.addressZip.value);
    return newOwner;
}

function copyAdditionalBeneficiaryFromMerchantApiResponse(apiResponse: MerchantRequestDto, index: number) {
    const newOwner = defaultBeneficiaryDetails()
    const flatApiResponse = FlattenUtil(apiResponse);
    Object.keys(newOwner)
        .forEach(key => {
            const indexedApiPath = newOwner[key].apiPath.replace("*", String(index));
            return newOwner[key].value = flatApiResponse[indexedApiPath];
        })
    const {formattedValue} = parsePhoneNumber(newOwner.phoneNumber);
    newOwner.phoneNumber = {...newOwner.phoneNumber, formattedValue}
    newOwner.addressZip.formattedValue = formatZip(newOwner.addressZip.value);
    return newOwner;
}

/**
 *
 * @param phoneNumberData format: "country_code phone_number" or "phone_number"
 */
const parsePhoneNumber = (phoneNumberData) => {
    const [countryCode, phoneNumber] = phoneNumberData.value?.split(" ") || [];
    phoneNumberData.countryCode = countryCode || AppDefaults.countryCode;
    const phoneNumberWithCountryCode = phoneNumber || phoneNumberData.value;
    const formattedValue = formatPhoneNumber(phoneNumberWithCountryCode)
    return {formattedValue, countryCode: countryCode || phoneNumberData.countryCode}
}

export const stakeholdersDetails = {
    namespaced: true,
    getters: businessProfileGetters,
    state: stakeholderDetailsDefaultState,
    actions,
    mutations,
};
